import { Pipeline } from 'app/model/pipeline/pipeline';
import { Dataset } from 'app/model/dataset/dataset';
import { AirflowEntity } from 'app/model/airflowEntity/airflowEntity';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { LinkedService } from 'app/model/linkedservice/linkedservice';

import { Metamodel } from 'app/model/base/model';
import { Trigger } from 'app/model/trigger/trigger';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { WranglingDataflow } from 'app/model/dataflow/wranglingDataflow';
import {
    DataMapper,
    Notebook,
    ScopeJobDefinition,
    SqlScript,
    KqlScript,
    SparkJobDefinition,
    ManagedPrivateEndpoint,
    Credential,
    Database,
    LinkTopic,
    SparkConfiguration
} from 'shell-models-lib';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { LoggerService } from 'app/logger/logger.service';
import { convertObjectToString } from './string.util';
import { Factory } from 'app/model/factory/factory';
import { GlobalParameter } from 'app/model/factory/global-parameter';
import { Flowlet } from 'app/model/dataflow/flowlet';
import { UserDefinedFunctionLibrary } from 'app/model/dataflow/user-defined-function-library';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';

export function getLinkedServiceConceptName(): string {
    return Metamodel.modelFor(LinkedService).name;
}

export function getHeadingForConceptName(conceptName: string): string {
    switch (conceptName) {
        case Metamodel.modelFor(Pipeline).name:
            return stringResources.Pipelines;

        case Metamodel.modelFor(Dataset).name:
            return stringResources.ModelName_DataSets;

        case Metamodel.modelFor(DataFlow).name:
            return stringResources.DataFlows;

        case Metamodel.modelFor(DataMapper).name:
            return stringResources.DataMapping;

        case Metamodel.modelFor(IntegrationRuntime).name:
            return stringResources.IntegrationRuntimes;

        case Metamodel.modelFor(LinkedService).name:
            return stringResources.LinkedServices;

        case Metamodel.modelFor(Trigger).name:
            return stringResources.Triggers;

        case Metamodel.modelFor(Notebook).name:
            return stringResources.NotebookActivityLabel;

        case Metamodel.modelFor(SqlScript).name:
            return stringResources.SqlScript;

        case Metamodel.modelFor(KqlScript).name:
            return stringResources.KqlScript;

        case Metamodel.modelFor(SparkJobDefinition).name:
            return stringResources.SynapseSparkJobDefinitionLabel;

        case Metamodel.modelFor(ScopeJobDefinition).name:
            return stringResources.SynapseScopeJobDefinitionLabel;

        case Metamodel.modelFor(Factory).name:
            return stringResources.DataFactoryProperties;

        case Metamodel.modelFor(ManagedPrivateEndpoint).name:
            return stringResources.ManagedPrivateEndpoints;

        case Metamodel.modelFor(Credential).name:
            return stringResources.AuthenticationType_UseCredentials;

        case Metamodel.modelFor(WranglingDataflow).name:
            return stringResources.PowerQuery;

        case Metamodel.modelFor(Database).name:
            return stringResources.Database;

        case Metamodel.modelFor(LinkTopic).name:
            return stringResources.LinkConnectionsLabel;

        case Metamodel.modelFor(AirflowEntity).name:
            return stringResources.AirflowEntities;

        case Metamodel.modelFor(SparkConfiguration).name:
            return stringResources.SparkConfiguration;

        case Metamodel.modelFor(AdfDataMapper).name:
            return stringResources.ADFDataMapperChangeDataCapturePreviewLabel;

        default:
            return '';
    }
}

export function getReadableResourceTypeForConceptName(conceptName: string): string {
    switch (conceptName) {
        case Metamodel.modelFor(Pipeline).name:
            return stringResources.Pipeline;

        case Metamodel.modelFor(Dataset).name:
            return stringResources.ModelName_DataSet;

        case Metamodel.modelFor(DataFlow).name:
            return stringResources.DataFlow;

        case Metamodel.modelFor(DataMapper).name:
            return stringResources.DataMapping;

        case Metamodel.modelFor(IntegrationRuntime).name:
            return stringResources.IntegrationRuntime;

        case Metamodel.modelFor(LinkedService).name:
            return stringResources.LinkedService;

        case Metamodel.modelFor(Trigger).name:
            return stringResources.Trigger;

        case Metamodel.modelFor(Notebook).name:
            return stringResources.NotebookActivityLabel;

        case Metamodel.modelFor(SqlScript).name:
            return stringResources.SqlScript;

        case Metamodel.modelFor(KqlScript).name:
            return stringResources.KqlScript;

        case Metamodel.modelFor(SparkJobDefinition).name:
            return stringResources.SynapseSparkJobDefinitionLabel;

        case Metamodel.modelFor(ScopeJobDefinition).name:
            return stringResources.SynapseScopeJobDefinitionLabel;

        case Metamodel.modelFor(Factory).name:
            return stringResources.DataFactoryProperties;

        case Metamodel.modelFor(ManagedPrivateEndpoint).name:
            return stringResources.ManagedPrivateEndpoint;

        case Metamodel.modelFor(Credential).name:
            return stringResources.AuthenticationType_UseCredentials;

        case Metamodel.modelFor(WranglingDataflow).name:
            return stringResources.PowerQuery;

        case Metamodel.modelFor(AirflowEntity).name:
            return stringResources.AirflowEntity;

        case Metamodel.modelFor(SparkConfiguration).name:
            return stringResources.SparkConfiguration;

        default:
            return '';
    }
}

export function resolveResourceType(resourceType: string): string {
    // Currently WranglingDataFlow is a separate type in Madrid. In backend it is actually a subtype of DataFlow.
    // TODO: [parask] - Have MappingDataFlow and WranglingDataFlow as subtypes of DataFlow and, the model
    // should get created based on the subtype (similar to Datasets and LS). This change will go in as separate PR
    return resourceType &&
        (resourceType.toLowerCase() === WranglingDataflow.MODEL.name.toLowerCase() ||
            resourceType.toLowerCase() === UserDefinedFunctionLibrary.MODEL.name.toLowerCase())
        ? DataFlow.MODEL.name
        : resourceType;
}

export function getIconByType(
    resourceType: string,
    subType: string,
    services: { lsRegistrySvc: LinkedServiceRegistryService; logger: LoggerService }
): string {
    resourceType = (resourceType || '').toLowerCase();
    switch (resourceType) {
        case Metamodel.modelFor(Pipeline).name.toLowerCase():
            return 'icon-i-pipeline';

        case Metamodel.modelFor(Dataset).name.toLowerCase():
            return 'icon-i-dataset';

        case Metamodel.modelFor(AdfDataMapper).name.toLowerCase():
            return 'icon-i_DataMapping_16x16';

        case Metamodel.modelFor(DataFlow).name.toLowerCase():
            return subType?.toLowerCase() === WranglingDataflow.MODEL.name.toLowerCase()
                ? 'icon-i-20x20-WranglingDF-Monochrome'
                : subType?.toLowerCase() === UserDefinedFunctionLibrary.MODEL.name.toLowerCase()
                ? 'svg-i_DataFlowLibraries_16x'
                : 'icon-i-20x20-MappingDF-Monochrome';

        case Metamodel.modelFor(Flowlet).name.toLowerCase():
            return 'icon-i_16x16-Flowlet-Monochrome';

        case Metamodel.modelFor(WranglingDataflow).name.toLowerCase():
            return 'icon-i-20x20-WranglingDF-Monochrome';

        case Metamodel.modelFor(LinkedService).name.toLowerCase():
            let iconClass = 'svg-i_linkedservice_black_16x16';
            try {
                const config = subType && services.lsRegistrySvc.getLinkedServiceConfiguration(subType);
                if (config && config.icon) {
                    iconClass = config.icon.className;
                }
            } catch (error) {
                // Won't happen unless there is an unknown linked service.
                services.logger.warn('getIconByType', convertObjectToString(error));
            }
            return iconClass;

        case Metamodel.modelFor(Trigger).name.toLowerCase():
            return 'icon-trigger';

        case Metamodel.modelFor(Notebook).name.toLowerCase():
            return 'icon-i-notebook-16x16 madrid-icon icon-size-16';

        case Metamodel.modelFor(ScopeJobDefinition).name.toLowerCase():
            return 'svg-i-scopejobdefinition-16x16';

        case Metamodel.modelFor(SqlScript).name.toLowerCase():
            return 'svg-i-sqlscripts-16x16';

        case Metamodel.modelFor(KqlScript).name.toLowerCase():
            return 'svg-i-kqlscripts-16x16';

        case Metamodel.modelFor(SparkJobDefinition).name.toLowerCase():
            return 'svg-i-sparkjobdefinition-16x16';

        case Metamodel.modelFor(GlobalParameter).name.toLowerCase():
            return 'icon-parameter-monitoring';

        case Metamodel.modelFor(Credential).name.toLowerCase():
            return 'icon-credentials';

        case Metamodel.modelFor(Database).name.toLowerCase():
            return 'svg-i-database-black-24x24';

        case Metamodel.modelFor(LinkTopic).name.toLowerCase():
            return 'madrid-icon icon-linkConnection icon-size-16';

        case Metamodel.modelFor(AirflowEntity).name.toLowerCase():
            return 'icon svg-airflow_16x16';

        case Metamodel.modelFor(SparkConfiguration).name.toLowerCase():
            return 'madrid-icon icon-i-spark-config';

        default:
            return '';
    }
}
